import { UseQueryOptions } from "react-query/types/react/types";

import { httpService } from "utils";
import { DateObject } from "utils/DateObject";
import { buildUrl } from "utils/queryParams";

import { useReactQuery } from "components/RemoteData";

import { IAppointmentListResponseDto } from "../application/types/IAppointmentListResponseDto";
import { IAppointmentListResponse } from "../application/types/IAppointmentListResponse";
import { useGetAuthorizationStore } from "../../authentication/infrastructure/useGetAuthorizationStore";

interface IUserAppointmentsParams {
  limit: number;
  offset: number;
  expired?: boolean;
}

export const useGetPaginatedUserAppointments = (
  params: IUserAppointmentsParams,
  options?: UseQueryOptions<IAppointmentListResponse>
) => {
  const { limit = 8, expired = false, offset = 0 } = params;
  const { userId, tokenSecurity } = useGetAuthorizationStore((state) => ({
    userId: state.userId,
    tokenSecurity: state.tokenSecurity,
  }));

  if (!userId) {
    throw new Error("UserId not found");
  }

  if (!tokenSecurity) {
    throw new Error("TokenSecurity not found");
  }

  return useReactQuery(
    getUserAppointmentsKey(userId, tokenSecurity, limit, offset, expired),
    () => getUserAppointments(userId, offset, limit, expired),
    options
  );
};

export const getUserAppointments = (
  userId: string,
  offset: number,
  limit: number,
  expired: boolean
): Promise<IAppointmentListResponse> => {
  return httpService()
    .get<IAppointmentListResponseDto>(
      buildUrl(`/e-reservation/user/${userId}/appointments`, {
        offset: offset,
        limit: limit,
        expired: expired,
      })
    )
    .then((appointments) => {
      return {
        ...appointments,
        userAppointments: appointments.userAppointments.map((appointment) => {
          return {
            ...appointment,
            createDate: DateObject(appointment.createDate),
            startDate: DateObject(appointment.startDate),
            endDate: DateObject(appointment.endDate),
          };
        }),
      };
    });
};

export const getUserAppointmentsKey = (
  userId: string,
  tokenSecurity: string,
  limit: number,
  offset: number,
  expired: boolean
) => {
  return ["user-appointments", userId, tokenSecurity, offset, limit, expired];
};
